import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { losApi } from './losApi';
import { BRAND } from './kit/tokens';
import { errorMessage } from './errors';
import { fmt$ } from './loan-file/shared/formFields';
import PickSelect from './shared/PickSelect';

// Ticket 193 D2: roll-ups over the pipeline (opportunities plus loan files no card
// links to). Each section loads on its own, so one failing report leaves the rest.
const WINDOWS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
];

// The server buckets by UTC month, so the keys are UTC too.
const monthKey = (offset) => {
  const d = new Date();
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1));
  return `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, '0')}`;
};
const PERIODS = [
  { value: '', label: 'All time' },
  { value: monthKey(0), label: 'This month' },
  { value: monthKey(-1), label: 'Last month' },
];

function useReport(load, key) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    load()
      .then((data) => { if (live) setState({ loading: false, error: null, data }); })
      .catch((e) => { if (live) setState({ loading: false, error: errorMessage(e), data: null }); });
    return () => { live = false; };
    // `load` is rebuilt every render; `key` says when the request really changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

function Section({ title, testId, state, action, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-serif font-semibold text-brand-navy">{title}</div>
        {action}
      </div>
      {state.loading && !state.data ? (
        <div className="text-slate-500 text-sm flex items-center gap-2 py-4"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : state.error ? (
        <div className="text-red-600 text-sm flex items-center gap-2 py-4" data-testid={`${testId}-error`}><AlertTriangle size={14} /> Couldn't load this report: {state.error}</div>
      ) : (
        <div className={state.loading ? 'opacity-50' : ''} aria-busy={state.loading}>{children(state.data)}</div>
      )}
    </div>
  );
}

function Select({ value, onChange, options, label, testId }) {
  return (
    <div className="w-44">
      <PickSelect
        label={label}
        labelClassName="sr-only"
        value={String(value)}
        onChange={onChange}
        options={options.map((o) => ({ value: String(o.value), label: o.label }))}
        testId={testId}
      />
    </div>
  );
}

function Stat({ label, value, hint, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-serif text-3xl font-bold mt-1" style={{ color }}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

const delta = (pct) => (pct == null ? 'no base month' : `${pct > 0 ? '+' : ''}${pct}%`);

export default function PerformanceTab() {
  const [days, setDays] = useState(30);
  const [period, setPeriod] = useState('');
  const kpi = useReport(() => losApi.reportKpi(days), `kpi-${days}`);
  const funnel = useReport(() => losApi.reportFunnel(), 'funnel');
  const board = useReport(() => losApi.reportLeaderboard(period), `board-${period}`);
  const periods = useReport(() => losApi.reportPeriods(), 'periods');
  const branches = useReport(() => losApi.reportBranches(), 'branches');

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="performance-tab">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-brand-navy">Performance</h1>
          <p className="text-sm text-slate-500">Pipeline, funded volume and who closed it. Funded = a won card, or a funded loan file with no card.</p>
        </div>
        <Select value={days} onChange={(v) => setDays(Number(v))} options={WINDOWS} label="Window" testId="perf-window" />
      </div>

      {kpi.error ? (
        <div className="text-red-600 text-sm flex items-center gap-2" data-testid="perf-kpi-error"><AlertTriangle size={14} /> Couldn't load the summary: {kpi.error}</div>
      ) : kpi.data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="perf-kpi">
          <Stat label="Open pipeline" value={fmt$(kpi.data.active_pipeline.total_volume)} hint={`${kpi.data.active_pipeline.count} deals`} color={BRAND.navy} />
          <Stat label="Funded" value={fmt$(kpi.data.closed_pipeline.total_volume)} hint={`${kpi.data.closed_pipeline.count} deals, last ${kpi.data.window_days} days`} color={BRAND.accent} />
          <Stat label="Avg days to fund" value={kpi.data.avg_cycle_days} hint="start to funded" color="#f59e0b" />
          <Stat label="Contacts" value={kpi.data.total_contacts} color="#64748b" />
        </div>
      ) : <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>}

      <Section title="Funded volume by month" testId="perf-periods" state={periods}>
        {(d) => (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">This month so far</div>
              <div className="font-semibold text-brand-navy" data-testid="perf-this-month">{fmt$(d.this_month.volume)} · {d.this_month.count} deals</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Last month</div>
              <div className="font-semibold text-brand-navy" data-testid="perf-prev-month">{fmt$(d.prev_month.volume)} · {d.prev_month.count} deals</div>
              <div className="text-xs text-slate-500" data-testid="perf-mom">{delta(d.delta_pct)} vs last month</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Same month last year</div>
              <div className="font-semibold text-brand-navy" data-testid="perf-last-year">{fmt$(d.same_month_last_year.volume)} · {d.same_month_last_year.count} deals</div>
              <div className="text-xs text-slate-500" data-testid="perf-yoy">{delta(d.yoy_delta_pct)} vs last year</div>
            </div>
          </div>
        )}
      </Section>

      <Section title="Pipeline funnel" testId="perf-funnel" state={funnel}>
        {(d) => {
          const max = Math.max(1, ...d.funnel.map((f) => f.count));
          return d.funnel.length === 0 ? <div className="text-slate-500 text-sm py-2">No open deals.</div> : (
            <div className="space-y-2">
              {d.funnel.map((f) => (
                <div key={f.stage} className="flex items-center gap-3 text-sm" data-testid={`perf-funnel-${f.stage}`}>
                  <div className="w-40 truncate text-slate-600">{f.label}</div>
                  <div className="flex-1 bg-slate-100 rounded h-3 overflow-hidden">
                    <div className="h-3 rounded" style={{ width: `${(f.count / max) * 100}%`, background: f.color || BRAND.accent }} />
                  </div>
                  <div className="w-10 text-right font-semibold text-brand-navy">{f.count}</div>
                </div>
              ))}
              {d.loan_files_without_card > 0 && (
                <div className="text-xs text-slate-500 pt-1" data-testid="perf-funnel-loans">{d.loan_files_without_card} open loan {d.loan_files_without_card === 1 ? 'file has' : 'files have'} no pipeline card, so {d.loan_files_without_card === 1 ? 'is' : 'are'} not in the funnel.</div>
              )}
            </div>
          );
        }}
      </Section>

      <Section title="Leaderboard" testId="perf-leaderboard" state={board} action={<Select value={period} onChange={setPeriod} options={PERIODS} label="Period" testId="perf-period" />}>
        {(d) => (d.items.length === 0 ? <div className="text-slate-500 text-sm py-2" data-testid="perf-board-empty">No funded deals in this period.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-500"><th className="py-1">#</th><th>Loan officer</th><th className="text-right">Deals</th><th className="text-right">Volume</th></tr></thead>
            <tbody>
              {d.items.map((i, n) => (
                <tr key={i.user_id} className="border-t border-slate-100" data-testid={`perf-board-${i.user_id}`}>
                  <td className="py-2 text-slate-500">{n + 1}</td>
                  <td className="font-semibold text-brand-navy">{i.name || i.email || (i.user_id === 'unassigned' ? 'Unassigned' : 'Former staff member')}</td>
                  <td className="text-right">{i.deals}</td>
                  <td className="text-right">{fmt$(i.volume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </Section>

      {/* One branch has nothing to compare, so the report stays out of sight until a second exists. */}
      {branches.data?.enabled && (
        <Section title="Branches" testId="perf-branches" state={branches}>
          {(d) => (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-500"><th className="py-1">Branch</th><th className="text-right">Members</th><th className="text-right">Active</th><th className="text-right">Funded</th></tr></thead>
              <tbody>
                {d.items.map((b) => (
                  <tr key={b.branch_id} className="border-t border-slate-100" data-testid={`perf-branch-${b.branch_id}`}>
                    <td className="py-2 font-semibold text-brand-navy">{b.name}</td>
                    <td className="text-right">{b.members}</td>
                    <td className="text-right">{b.active_deals}</td>
                    <td className="text-right">{b.closed_deals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}
    </div>
  );
}
